/**
 * @file: DeliveryCheckManager.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { isDebugEnabled } from '@euler/functions/debug';
import { getFuelTypeByRawValue } from '@euler/functions/getFuelTypeByRawValue';
import { matchDeliveryCheckTemplate } from '@euler/functions/matchDeliveryCheckTemplate';
import {
  deleteCachedFile,
  readCachedFileContents,
  writeContentsToCachedFile,
} from '@euler/lib/cache/cacheUtil';
import {
  DeliveryCheckTemplate,
  DeliveryCheckTemplateItem,
  DeliveryCheckWorkItem,
} from '@euler/model';
import { CommonTaskStatus, DeliveryCheckSceneType } from '@euler/model/enum';
import {
  CachedTaskDetail,
  DeliveryCheckBase,
  DeliveryCheckCommitPayload,
  DeliveryCheckItemStaged,
  DeliveryCheckStaged,
  TaskDetailVersions,
  TaskMediaStaged,
  VersionedResponse,
} from '@euler/model/task-detail';
import {
  ReworkRecordCommitPayload,
  ReworkRecordStaged,
} from '@euler/model/task-detail/ReworkRecord';
import { comparerDesc, isNotNull, makeDebug } from '@euler/utils';
import { array2map, sorted } from '@euler/utils/array';
import produce, { Immutable, freeze } from 'immer';
import { WritableDraft } from 'immer/dist/internal';
import { nanoid } from 'nanoid/non-secure';
import { BehaviorSubject, Subscription, debounceTime, skip } from 'rxjs';
import {
  MediaUploadTask,
  MediaUploadTaskEvent,
} from '../MediaFileUploadController';
import { TaskWorkflowNodeManager } from '../TaskWorkflowNodeManager';
import { ITaskManager, TaskFileUploadRequestContext } from '../types';
import {
  anyMediaToMediaStaged,
  defaultDeliveryCheckItemStagedOptions,
  deliveryCheckTemplateSceneTypeToDeliveryCheckSceneType,
  mediaStagedToCommitPayload,
} from '../util';

const debug = makeDebug(
  'delivery-check-manager:base',
  isDebugEnabled('taskManager'),
);

export abstract class DeliveryCheckManagerBase<
  T extends DeliveryCheckBase = DeliveryCheckBase,
> extends TaskWorkflowNodeManager {
  staged$!: BehaviorSubject<DeliveryCheckStaged>;
  readonly defaultMode: DeliveryCheckStaged['mode'] = 'list';
  readonly dirty$: BehaviorSubject<boolean>;
  protected readonly cacheFilePath: string;
  protected changeSubscription?: Subscription;
  protected removeFileUploadListener: (() => void) | undefined;
  protected template: DeliveryCheckTemplate | undefined;
  protected templateSubjectToItemMap = new Map<
    string,
    DeliveryCheckTemplateItem
  >();

  constructor(
    taskManager: ITaskManager,
    readonly sceneType: DeliveryCheckSceneType,
  ) {
    super(taskManager);

    this.dirty$ = new BehaviorSubject<boolean>(false);

    this.cacheFilePath = this.taskManager.getCacheFilePath(
      sceneType === DeliveryCheckSceneType.CompletionCheck
        ? 'delivery-checks.json'
        : 'sampling-checks.json',
    );
  }

  async prepare(): Promise<void> {
    // load delivery check template
    const tags = this.cachedDetail.basicInfo.tags?.toString();
    const hasInspection =
      this.cachedDetail.inspection.status == CommonTaskStatus.Finished;
    const hasConstruction =
      this.cachedDetail.construction.status == CommonTaskStatus.Finished;
    const templates = await this.inventoryService.getDeliveryCheckTemplates();
    const fuelType = getFuelTypeByRawValue(
      this.cachedDetail.basicInfo.licensePlateNo,
      this.cachedDetail.basicInfo.vehicleFuelType,
    );

    this.template = matchDeliveryCheckTemplate(
      templates.filter(
        x =>
          deliveryCheckTemplateSceneTypeToDeliveryCheckSceneType(
            x.sceneType,
          ) === this.sceneType,
      ),
      {
        agentId: this.taskManager.currentUser.agent?.id,
        orgId: this.currentUser.orgId,
        storeId: this.currentUser.storeId,
        hasInspection,
        hasConstruction,
        tags,
        fuelType,
      },
    );

    if (
      this.template == null &&
      this.sceneType === DeliveryCheckSceneType.SamplingCheck
    ) {
      // fallback to completion check template
      this.template = matchDeliveryCheckTemplate(templates, {
        agentId: this.taskManager.currentUser.agent?.id,
        orgId: this.currentUser.orgId,
        storeId: this.currentUser.storeId,
        hasInspection,
        hasConstruction,
        tags,
        fuelType,
      });
    }

    this.templateSubjectToItemMap = array2map(
      this.template?.conf?.items ?? [],
      x => x.subject,
    );

    if (this.removeFileUploadListener == null) {
      this.removeFileUploadListener =
        this.taskManager.uploadController.addListener((e, task) => {
          this.onFileUploadEvent(e, task as any);
        });
    }

    this.stopObserveChanges();

    const targetDeliveryCheck = this.getDeliveryCheck(this.cachedDetail);
    const staged = this.toStaged(targetDeliveryCheck, x => {
      x.mode = targetDeliveryCheck.items.length > 0 ? 'list' : this.defaultMode;
      x.currentIndex = 0;
    });

    await this.internalPrepare(staged);

    if (staged.items.length < 5) {
      staged.mode = 'list';
    }

    this.staged$ = new BehaviorSubject(staged);

    this.startObserveChanges();
  }

  async syncWorkItems() {
    const workItems = await this.taskService.getDeliveryCheckWorkItems(
      this.taskNo,
      this.sceneType,
    );
    let newKeys: { key: string; index: number }[] = [];
    this.produce(draft => {
      newKeys = this.internalSyncWorkItems(draft, workItems);
    });
    return newKeys;
  }

  async begin() {
    const resp = await this.taskService.beginDeliveryChecks(
      this.taskNo,
      this.sceneType,
    );
    this.handleVersionedResponse(resp, (_, { deliveryCheck }) => {
      if (deliveryCheck.status === CommonTaskStatus.Pending) {
        deliveryCheck.status = CommonTaskStatus.InProgress;
        deliveryCheck.startedAt = new Date();
        deliveryCheck.startedBy = this.currentUser.name;
      }
    });
  }

  async loadCached(): Promise<DeliveryCheckStaged | undefined> {
    const contents = await readCachedFileContents(this.cacheFilePath);
    if (contents == null) return undefined;
    return JSON.parse(contents) as DeliveryCheckStaged;
  }

  applyCached(cached: DeliveryCheckStaged) {
    this.withChangeObserverTemporarilyDisabled(() => this.staged$.next(cached));
  }

  async discardCache() {
    await deleteCachedFile(this.cacheFilePath, true);
  }

  reset() {
    this.withChangeObserverTemporarilyDisabled(() => {
      const targetDeliveryCheck = this.getDeliveryCheck(this.cachedDetail);
      this.resetWithDeliveryCheck(targetDeliveryCheck);
    });
  }

  async resetAndClearCache() {
    await this.discardCache();
    this.reset();
  }

  cleanUp() {
    this.removeFileUploadListener?.();
    this.removeFileUploadListener = undefined;
    this.stopObserveChanges();
  }

  dispose() {
    this.cleanUp();
  }

  removeMedia(itemKey: string, key: string) {
    this.taskManager.uploadController.abort(key);
    this.produceItem(itemKey, (item, original) => {
      const k = original.medias.findIndex(x => x._key === key);
      if (k < 0) {
        console.error(new Error('Cannot find target media, this must be bug'));
        return;
      }
      item.medias.splice(k, 1);
    });
  }

  findMedia(itemKey: string, key: string) {
    const current = this.staged$.getValue();
    const item = current.items.find(x => x._key === itemKey);
    if (!item) return undefined;
    return item.medias.find(x => x._key === key);
  }

  enumerate(callbacks: {
    onItem?: (item: DeliveryCheckItemStaged) => boolean | undefined;
    onMedia?: (
      media: TaskMediaStaged,
      item: DeliveryCheckItemStaged,
    ) => boolean | undefined;
  }) {
    const staged = this.staged$.getValue();
    for (const item of staged.items) {
      if (false === callbacks.onItem?.(item)) {
        break;
      }
      let shouldSkip = false;
      for (const media of item.medias) {
        if (false === callbacks.onMedia?.(media, item)) {
          shouldSkip = true;
          break;
        }
      }
      if (shouldSkip) break;
    }
  }

  produce(
    recipe: (
      draft: WritableDraft<DeliveryCheckStaged>,
      original: DeliveryCheckStaged,
    ) => void,
  ) {
    const current = freeze(this.staged$.getValue());
    const nextState = produce(current, draft => {
      recipe(draft, current);
    });
    if (nextState !== current) {
      this.staged$.next(nextState);
    }
  }

  produceItem(
    key: string,
    recipe: (
      draft: WritableDraft<DeliveryCheckItemStaged>,
      original: DeliveryCheckItemStaged,
      originalState: DeliveryCheckStaged,
      index: number,
    ) => void,
    options?: { ignoreError?: boolean },
  ) {
    const current = this.staged$.getValue();
    const index = current.items.findIndex(x => x._key === key);
    if (index < 0) {
      if (!options?.ignoreError) {
        console.error(new Error('Cannot find check item, this must be bug'));
      }
      return;
    } else {
      this.produceItemAt(index, recipe);
    }
  }

  produceItemAt(
    index: number,
    recipe: (
      draft: WritableDraft<DeliveryCheckItemStaged>,
      original: DeliveryCheckItemStaged,
      originalState: DeliveryCheckStaged,
      index: number,
    ) => void,
  ) {
    this.produce((draft, original) => {
      const item = draft.items[index];
      recipe(item, original.items[index], original, index);
      item.timestamp = new Date();
    });
  }

  getCommitPayload(ignoreUncheckedItems?: boolean): DeliveryCheckCommitPayload {
    const staged = this.staged$.getValue();
    return {
      sceneType: this.sceneType,
      items: staged.items.map(item => {
        if (!ignoreUncheckedItems && (!item.resultCode || !item.resultText)) {
          throw new Error('Delivery check item must be checked');
        }
        return {
          id: item.id,
          title: item.title,
          constructionJobId: item.constructionJobId,
          resultCode: item.resultCode || 'NOT_ACCEPTED',
          resultText: item.resultText,
          remark: item.remark || item.defaultRemark,
          unacceptedReason: item.unacceptedReason,
          requiresPhoto: item.requiresPhoto,
          medias: item.medias.map(mediaStagedToCommitPayload).filter(isNotNull),
          reworkRecords: item.reworkRecords.map<ReworkRecordCommitPayload>(
            record => ({
              id: record.id,
              responsiblePersonId: record.responsiblePersonId!,
              responsiblePersonName: record.responsiblePersonName!,
              remarks: record.remarks,
              medias: record.medias
                .map(mediaStagedToCommitPayload)
                .filter(isNotNull),
            }),
          ),
        };
      }),
    };
  }

  setMode(mode: DeliveryCheckStaged['mode']) {
    this.produce(draft => {
      if (draft.mode !== mode) {
        draft.mode = mode;
      }
    });
  }

  protected toStaged(
    deliveryCheck: T,
    update?: (staged: DeliveryCheckStaged) => void,
  ) {
    const map = this.templateSubjectToItemMap;

    const technicianMap = array2map(
      sorted(deliveryCheck.items, (x, y) =>
        comparerDesc(x.timestamp, y.timestamp),
      ),
      x => x.technicianId,
      x => x.technicianName,
    );
    const latestTechnician = [...technicianMap.entries()][0];

    const staged: DeliveryCheckStaged = {
      mode: 'wizard',
      currentIndex: 0,
      technicianId: latestTechnician?.[0] ?? this.currentUser.memberId,
      technicianName: latestTechnician?.[1] ?? this.currentUser.name,
      items: deliveryCheck.items.map<DeliveryCheckItemStaged>(x => {
        const options =
          map.get(x.title)?.options ??
          defaultDeliveryCheckItemStagedOptions(x.constructionJobId);

        const selectedOptionIndex = options.findIndex(
          t => t.title === x.resultText,
        );

        const templateItem = this.template?.conf?.items.find(
          p => p.subject === x.title,
        );

        const item: DeliveryCheckItemStaged = {
          _key: nanoid(),
          id: x.id,
          title: x.title,
          timestamp: new Date(x.timestamp),
          authorId: x.technicianId,
          authorName: x.technicianName,
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          constructionJobId: x.constructionJobId || undefined,
          resultCode: x.resultCode,
          resultText: x.resultText,
          unacceptedReason: x.unacceptedReason,
          remark: x.remark,
          defaultRemark:
            selectedOptionIndex >= 0
              ? options[selectedOptionIndex]?.remark
              : undefined,
          requiresPhoto: x.requiresPhoto,
          removable: templateItem?.removable,
          allowChooseMediaFromGallery:
            templateItem?.allowChooseMediaFromGallery,
          medias: x.medias.map(anyMediaToMediaStaged),
          selectedOptionIndex:
            selectedOptionIndex >= 0 ? selectedOptionIndex : undefined,
          options,
          reworkRecords:
            x.reworkRecords?.map<ReworkRecordStaged>(record => ({
              _key: nanoid(),
              sceneType: this.sceneType,
              id: record.id,
              licensePlateNo: record.licensePlateNo,
              vehicleName: record.vehicleName,
              vehicleBrandName: record.vehicleBrandName,
              vehicleModel: record.vehicleModel,
              vehicleMileage: record.vehicleMileage,
              serviceAgentId: record.serviceAgentId,
              serviceAgentName: record.serviceAgentName,
              constructionJobId: record.constructionJobId,
              checkTitle: record.checkTitle,
              checkTime: record.checkTime,
              checkAuthorId: record.checkAuthorId,
              checkAuthorName: record.checkAuthorName,
              responsiblePersonId: record.responsiblePersonId,
              responsiblePersonName: record.responsiblePersonName,
              remarks: record.remarks,
              serviceCount: record.serviceCount,
              createdAt: record.createdAt,
              medias: record.medias.map(anyMediaToMediaStaged),
            })) ?? [],
        };

        this.makeConstructionWorkItemCheckedByDefault(item);

        return item;
      }),
    };
    update?.(staged);
    return staged;
  }

  protected resetWithDeliveryCheck(deliveryCheck: T) {
    const staged = this.staged$.getValue();
    this.staged$.next(
      this.toStaged(deliveryCheck, x => {
        // keep the following properties untouched
        x.mode = staged.mode;
        x.currentIndex = staged.currentIndex;
        x.technicianId = staged.technicianId;
        x.technicianName = staged.technicianName;
      }),
    );
    if (this.dirty$.getValue()) {
      this.dirty$.next(false);
    }
  }

  protected startObserveChanges() {
    if (this.changeSubscription) {
      return;
    }

    debug('changes observer started');
    this.changeSubscription = this.staged$
      .pipe(skip(1), debounceTime(500))
      .subscribe(staged => {
        if (!this.dirty$.getValue()) {
          this.dirty$.next(true);
        }
        void writeContentsToCachedFile(
          this.cacheFilePath,
          JSON.stringify(staged, null, 2),
        );
        debug('delivery checks written to cache');
      });
  }

  protected stopObserveChanges() {
    if (this.changeSubscription) {
      this.changeSubscription.unsubscribe();
      this.changeSubscription = undefined;
      debug('changes observer stopped');
    }
  }

  protected withChangeObserverTemporarilyDisabled(fn: () => any): any {
    this.stopObserveChanges();
    const result = fn();
    this.startObserveChanges();
    return result;
  }

  protected handleVersionedResponse<U>(
    versionedResponse: VersionedResponse<U>,
    recipe: (
      draft: WritableDraft<CachedTaskDetail>,
      context: {
        response: U;
        deliveryCheck: T;
        version: number;
        original: T;
      },
    ) => void,
  ) {
    return this.produceWithVersions(
      versionedResponse.versions,
      (draft, context) => {
        recipe(draft, { ...context, response: versionedResponse.response });
      },
    );
  }

  protected produceWithVersions(
    versions: TaskDetailVersions,
    recipe: (
      draft: WritableDraft<CachedTaskDetail>,
      context: {
        deliveryCheck: T;
        version: number;
        original: T;
      },
    ) => void,
  ) {
    const original = this.getDeliveryCheck(this.cachedDetail);
    const version = this.getDeliveryCheckVersion(versions);

    if (
      version === original.version &&
      this.cachedDetail.version === versions.detail
    ) {
      // nothing changed
      return this.cachedDetail;
    }

    return this.taskManager.produceWithVersions(versions, draft => {
      const targetDeliveryCheck = this.getDeliveryCheck(draft);
      recipe(draft, {
        deliveryCheck: targetDeliveryCheck,
        version,
        original,
      });
      if (targetDeliveryCheck.version !== version) {
        targetDeliveryCheck.version = version;
      }
    });
  }

  protected makeConstructionWorkItemCheckedByDefault(
    item: DeliveryCheckItemStaged,
  ) {
    if (
      item.constructionJobId != null &&
      item.constructionJobId > 0 &&
      (item.selectedOptionIndex == null || item.selectedOptionIndex < 0)
    ) {
      const expectedIndex = item.options.findIndex(x => x.isExpected);
      const k = item.resultText
        ? item.options.findIndex(x => x.title === item.resultText)
        : expectedIndex;
      if (k >= 0) {
        item.selectedOptionIndex = k;
        item.resultCode = k === expectedIndex ? 'OK' : 'NOT_ACCEPTED';
        item.resultText =
          item.options[k].title ??
          (k === expectedIndex ? '检查合格' : '不合格');
      }
    }
  }

  protected async internalPrepare(staged: DeliveryCheckStaged) {
    const workItems = await this.taskService.getDeliveryCheckWorkItems(
      this.taskNo,
      this.sceneType,
    );

    if (staged.items.length > 0) {
      this.internalSyncWorkItems(staged, workItems);
      return;
    }

    const items: DeliveryCheckItemStaged[] = [];

    const existing = new Set(workItems.map(x => x.title));

    // delivery check items from template
    if (this.template?.conf != null) {
      for (const item of this.template.conf.items) {
        if (existing.has(item.subject)) {
          continue;
        }
        const selectedOptionIndex = item.options?.findIndex(
          x => x.isDefaultChecked,
        );
        const defaultSelected =
          selectedOptionIndex != null && selectedOptionIndex >= 0
            ? item.options?.[selectedOptionIndex]
            : undefined;
        items.push({
          _key: nanoid(),
          id: undefined,
          title: item.subject,
          timestamp: undefined,
          authorId: undefined,
          authorName: undefined,
          requiresPhoto: item.requiresPhoto,
          constructionJobId: undefined,
          options: item.options ?? defaultDeliveryCheckItemStagedOptions(),
          resultCode: defaultSelected?.isExpected ? 'OK' : 'NOT_ACCEPTED',
          resultText: defaultSelected?.title,
          unacceptedReason: undefined,
          selectedOptionIndex:
            defaultSelected?.isExpected &&
            selectedOptionIndex != null &&
            selectedOptionIndex >= 0
              ? selectedOptionIndex
              : undefined,
          medias: [],
          reworkRecords: [],
          remark: undefined,
          defaultRemark: defaultSelected?.remark,
          removable: item.removable,
          allowChooseMediaFromGallery: item.allowChooseMediaFromGallery,
        });
      }
    }

    // delivery check items from construction jobs
    for (const workItem of workItems) {
      items.push(this.workItemToItemStaged(workItem));
    }

    for (const item of items) {
      if (item.constructionJobId != null) {
        this.makeConstructionWorkItemCheckedByDefault(item);
      }
    }

    if (items.some(x => x.constructionJobId != null)) {
      this.dirty$.next(true);
    }

    staged.items = items;
  }

  private workItemToItemStaged(
    workItem: DeliveryCheckWorkItem,
  ): DeliveryCheckItemStaged {
    const options =
      this.templateSubjectToItemMap.get(workItem.title)?.options ??
      defaultDeliveryCheckItemStagedOptions(workItem.constructionJobId);
    const templateItem = this.template?.conf?.items.find(
      p => p.subject === workItem.title,
    );
    return {
      _key: nanoid(),
      id: undefined,
      title: workItem.title,
      timestamp: undefined,
      authorId: undefined,
      authorName: undefined,
      resultCode: undefined,
      resultText: undefined,
      unacceptedReason: undefined,
      requiresPhoto: workItem.requiresPhoto,
      constructionJobId: workItem.constructionJobId,
      medias: workItem.medias.map(anyMediaToMediaStaged),
      options,
      reworkRecords: [],
      remark: undefined,
      removable: templateItem?.removable,
      allowChooseMediaFromGallery: templateItem?.allowChooseMediaFromGallery,
    };
  }

  private onFileUploadEvent(
    e: MediaUploadTaskEvent,
    task: Immutable<MediaUploadTask<TaskFileUploadRequestContext>>,
  ) {
    const request = task.request;
    const context = request.context;

    // ignore unwanted event that is not specific to me.
    if (context == null || context.type !== this.getUploadEventContextType()) {
      return;
    }

    if (e.type === 'queued') {
      // add a new media state when queued.
      this.produceItem(context.key, (draft, original) => {
        if (context.mediaKey) {
          const index = original.medias.findIndex(
            x => x._key === context.mediaKey,
          );
          if (index >= 0) {
            const media = draft.medias[index];
            media.annotationMetadata = context.annotationMetadata as any;
            media._source = {
              type: 'local',
              localFileUri: request.localFileUri,
              contentType: request.contentType,
              uploadParameters: request.parameters,
              state: { status: 'queued' },
            };
            return;
          }
        }

        draft.medias.push({
          type: request.contentType,
          annotationMetadata: context.annotationMetadata as any,
          _key: context.mediaKey ?? task.key,
          _timestamp: Date.now(),
          _source: {
            type: 'local',
            localFileUri: request.localFileUri,
            contentType: request.contentType,
            uploadParameters: request.parameters,
            state: { status: 'queued' },
          },
        });
      });
    } else {
      // update the existing media state.
      this.produceItem(context.key, (draft, original) => {
        // use original item to speed up searching
        const index = original.medias.findIndex(
          x => x._key === (context.mediaKey ?? task.key),
        );
        if (index < 0) return;
        const media = draft.medias[index];
        if (media._source.type !== 'local') return;
        switch (e.type) {
          case 'compressing': {
            media._source.state = {
              status: 'compressing',
              progress: e.progress,
            };
            return;
          }
          case 'begin': {
            media._source.state = { status: 'uploading', progress: 0 };
            return;
          }
          case 'progress': {
            media._source.state = {
              status: 'uploading',
              progress: e.progress,
            };
            return;
          }
          case 'error': {
            media._source.state = { status: 'error', error: e.error };
            return;
          }
          case 'abort': {
            if (task.status !== 'aborted') {
              media._source.state = {
                status: 'error',
                error: e.error ?? new Error('File uploading has aborted'),
              };
            }
            return;
          }
          case 'finished': {
            const cover = e.result.extra?.cover;
            media.coverUrl = cover != null && cover !== '' ? cover : undefined;
            media._source = {
              type: 'remote',
              url: e.result.url,
              cid: e.result.name,
            };
            return;
          }
          default:
            return;
        }
      });
    }
  }

  private internalSyncWorkItems(
    staged: DeliveryCheckStaged,
    workItems: DeliveryCheckWorkItem[],
  ): { key: string; index: number }[] {
    const workItemsWithConstructionJobs = workItems.filter(x =>
      isNotNull(x.constructionJobId),
    );
    const newKeys: { key: string; index: number }[] = [];

    const mostRecentConstructionIdSet = new Set(
      workItemsWithConstructionJobs.map(x => x.constructionJobId!),
    );

    // remove items that are not existed.
    if (mostRecentConstructionIdSet.size > 0) {
      staged.items = staged.items.filter(
        item =>
          item.constructionJobId == null ||
          mostRecentConstructionIdSet.has(item.constructionJobId),
      );
    }

    // add missing construction job items.
    for (const workItem of workItemsWithConstructionJobs) {
      const constructionJobId = workItem.constructionJobId!;

      let item = staged.items.find(
        x => x.constructionJobId === constructionJobId,
      );

      if (!item) {
        item = this.workItemToItemStaged(workItem);
        newKeys.push({ key: item._key, index: staged.items.length });
        this.dirty$.next(true);
        staged.items.push(item);
      }

      if (item != null) {
        this.makeConstructionWorkItemCheckedByDefault(item);
      }
    }

    return newKeys;
  }

  abstract save(options?: { saveOnly?: boolean }): Promise<void>;

  protected abstract getDeliveryCheck(detail: CachedTaskDetail): T;
  protected abstract getDeliveryCheckVersion(
    versions: TaskDetailVersions,
  ): number;
  protected abstract getUploadEventContextType(): Extract<
    TaskFileUploadRequestContext['type'],
    'delivery-check' | 'sampling-check'
  >;
}
